import React, { FC, ReactNode, useEffect, useState } from 'react'

import cn from 'classnames'
import { Swiper, SwiperSlide } from 'swiper/react'

import classes from './tabs.module.scss'

/***
 * Design system: https://www.figma.com/file/RE9n7PIwHG7A7WHA7kEVhN/WEB-Components?node-id=1011:126561&mode=dev
 * */

interface TabProps {
  label: string
  content: ReactNode
  trackingProps?: Record<string, unknown>
}

interface TabsProps {
  tabs: TabProps[]
  header?: ReactNode
  defaultIndex?: number
  defaultTab?: string
  alignment?: 'center' | 'left'
  className?: string
  dataTestId?: string
}

export const Tabs: FC<TabsProps> = ({
  tabs,
  defaultIndex = 0,
  defaultTab,
  header,
  alignment = 'left',
  className,
  dataTestId = 'tabs',
}) => {
  const [selectedIndex, setSelectedIndex] = useState(() => {
    if (defaultTab) {
      const tabIndex = tabs.findIndex((tab) => tab.label === defaultTab)
      return tabIndex >= 0 ? tabIndex : 0
    }
    return defaultIndex >= tabs.length ? 0 : defaultIndex
  })

  useEffect(() => {
    if (defaultTab) {
      const tabIndex = tabs.findIndex((tab) => tab.label === defaultTab)
      setSelectedIndex(tabIndex >= 0 ? tabIndex : 0)
    }
  }, [defaultTab, tabs])

  const onSelect = (index: number) => () => {
    setSelectedIndex(index)
  }

  return (
    <>
      <Swiper
        data-testid={dataTestId}
        watchOverflow
        allowTouchMove
        navigation={false}
        slidesPerView="auto"
        spaceBetween={0}
        className={cn(
          classes.root,
          {
            [classes.centerAligned]: alignment === 'center',
          },
          className
        )}
      >
        {tabs.map(({ label, trackingProps }, index) => (
          <SwiperSlide key={label}>
            <a
              onClick={onSelect(index)}
              className={cn(classes.tab, {
                [classes.selected]: index === selectedIndex,
              })}
              tabIndex={0}
              {...trackingProps}
            >
              {label}
            </a>
          </SwiperSlide>
        ))}
      </Swiper>
      {header}
      <div className={classes.tabContent} key={selectedIndex}>
        {tabs[selectedIndex].content}
      </div>
    </>
  )
}
